import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, MapPin, Star, Clock, Phone, MessageCircle, Award, Users, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const CoachesPage = () => {
  const { API, user } = useContext(AuthContext);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArea, setSelectedArea] = useState('הכל');
  const [selectedSpecialty, setSelectedSpecialty] = useState('הכל');
  const [searchTerm, setSearchTerm] = useState('');

  const areas = [
    { id: 'הכל', label: 'כל האזורים', icon: '🌍' },
    { id: 'צפון', label: 'צפון', icon: '🏔️' },
    { id: 'שרון', label: 'שרון', icon: '🌊' },
    { id: 'מרכז', label: 'מרכז', icon: '🏙️' },
    { id: 'ירושלים', label: 'ירושלים', icon: '🕌' },
    { id: 'דרום', label: 'דרום', icon: '🏜️' },
  ];

  const specialties = [
    { id: 'הכל', label: 'כל ההתמחויות' },
    { id: 'ילדים', label: 'ילדים ונוער' },
    { id: 'מתחילים', label: 'מתחילים' },
    { id: 'מתקדמים', label: 'מתקדמים' },
    { id: 'מבוגרים', label: 'מבוגרים' },
    { id: 'תחרותי', label: 'טניס תחרותי' },
  ];

  useEffect(() => {
    fetchCoaches();
  }, [selectedArea, selectedSpecialty]);

  const fetchCoaches = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedArea !== 'הכל') params.append('area', selectedArea);
      if (selectedSpecialty !== 'הכל') params.append('specialty', selectedSpecialty);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API}/coaches${query}`);
      setCoaches(response.data);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCoaches = searchTerm
    ? coaches.filter(c =>
        c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.city?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : coaches;

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : name.substring(0, 2);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  const formatWhatsApp = (number) => {
    if (!number) return '';
    let clean = number.replace(/[^0-9+]/g, '');
    if (clean.startsWith('+972')) clean = '972' + clean.slice(4);
    else if (clean.startsWith('972')) clean = clean;
    else if (clean.startsWith('0')) clean = '972' + clean.slice(1);
    else clean = '972' + clean;
    return clean;
  };

  const getWhatsAppNumber = (coach) => {
    if (coach.whatsapp) return formatWhatsApp(coach.whatsapp);
    if (coach.phone) return formatWhatsApp(coach.phone);
    return '';
  };

  const isAdmin = ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user?.email);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center shadow-lg">
              <Award className="w-9 h-9 text-white" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent mb-4" data-testid="coaches-title">
            מצא מאמן טניס
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            המאמנים הטובים ביותר בישראל במקום אחד. מצא את המאמן המושלם עבורך והתחל להשתפר!
          </p>
        </div>

        {/* Search */}
        <div className="mb-8 max-w-md mx-auto">
          <div className="relative">
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="חפש מאמן לפי שם או עיר..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-12 py-6 text-lg rounded-2xl border-2 border-gray-200 focus:border-emerald-500 shadow-lg"
              data-testid="coach-search-input"
            />
          </div>
        </div>

        {/* Area Filter */}
        <div className="mb-6" data-testid="area-filter">
          <div className="flex flex-wrap justify-center gap-3">
            {areas.map((area) => (
              <button
                key={area.id}
                onClick={() => setSelectedArea(area.id)}
                data-testid={`area-tab-${area.id}`}
                className={`
                  px-5 py-3 rounded-xl font-bold text-sm transition-all duration-300 transform
                  flex items-center gap-2 shadow-md hover:shadow-lg hover:-translate-y-1
                  ${selectedArea === area.id
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
                  }
                `}
              >
                <span className="text-lg">{area.icon}</span>
                {area.label}
              </button>
            ))}
          </div>
        </div>

        {/* Specialty Filter */}
        <div className="mb-10" data-testid="specialty-filter">
          <div className="flex flex-wrap justify-center gap-2">
            {specialties.map((spec) => (
              <button
                key={spec.id}
                onClick={() => setSelectedSpecialty(spec.id)}
                data-testid={`specialty-tab-${spec.id}`}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                  ${selectedSpecialty === spec.id
                    ? 'bg-teal-100 text-teal-800 border-2 border-teal-400'
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                  }
                `}
              >
                {spec.label}
              </button>
            ))}
          </div>
        </div>

        {/* CTA - הירשם כמאמן */}
        <div className="mb-10 text-center">
          <Link to="/coaches/register">
            <Button
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold text-lg px-8 py-6 rounded-2xl shadow-lg hover:shadow-xl transition-all"
              data-testid="become-coach-btn"
            >
              <Award className="w-5 h-5 ml-2" />
              הירשם כמאמן באתר
            </Button>
          </Link>

          {/* Admin link */}
          {isAdmin && (
            <Link to="/admin/coaches" className="block mt-3">
              <Button variant="outline" className="border-2 border-purple-400 text-purple-600 hover:bg-purple-50" data-testid="admin-coaches-btn">
                ניהול מאמנים (אדמין)
              </Button>
            </Link>
          )}
        </div>

        {/* Coaches Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-20 h-20 rounded-full bg-gray-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-full" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredCoaches.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
              <Users className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">אין מאמנים עדיין</h3>
            <p className="text-gray-500 mb-6">היה הראשון להירשם כמאמן באתר!</p>
            <Link to="/coaches/register">
              <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold px-6 py-3 rounded-xl">
                הירשם כמאמן
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="coaches-grid">
            {filteredCoaches.map((coach) => (
              <Link to={`/coaches/${coach.id}`} key={coach.id}>
                <Card
                  className="h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-2 border-2 border-gray-100 hover:border-emerald-300 overflow-hidden group cursor-pointer"
                  data-testid={`coach-card-${coach.id}`}
                >
                  {/* Card Header Gradient */}
                  <div className="h-2 bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500" />

                  <CardContent className="p-6">
                    {/* Coach Info Top */}
                    <div className="flex items-start gap-4 mb-4">
                      <Avatar className="h-20 w-20 ring-4 ring-emerald-100 shadow-lg">
                        <AvatarImage src={coach.image_thumb || coach.image} alt={coach.full_name} />
                        <AvatarFallback className="bg-gradient-to-br from-emerald-400 to-teal-600 text-white text-xl font-bold">
                          {getInitials(coach.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold text-gray-900 group-hover:text-emerald-600 transition-colors truncate">
                          {coach.full_name}
                        </h3>
                        <div className="flex items-center gap-1 text-gray-500 mt-1">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm">{coach.city} | {coach.area}</span>
                        </div>
                        {/* Rating */}
                        <div className="flex items-center gap-1 mt-2">
                          {renderStars(coach.average_rating)}
                          <span className="text-sm text-gray-500 mr-1">
                            ({coach.total_reviews})
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                      {coach.description || 'מאמן טניס מקצועי'}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(coach.specialties || []).slice(0, 3).map((spec, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium border border-emerald-200"
                        >
                          {spec}
                        </span>
                      ))}
                    </div>

                    {/* Bottom Info */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1 text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{coach.experience_years} שנות ניסיון</span>
                      </div>
                      {coach.club && (
                        <span className="text-teal-600 text-sm font-medium truncate max-w-[120px]">
                          {coach.club}
                        </span>
                      )}
                    </div>

                    {/* Prices */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {coach.price_private && (
                        <span className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-200 font-medium">
                          פרטי: {coach.price_private}
                        </span>
                      )}
                      {coach.price_duo && (
                        <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-lg border border-blue-200 font-medium">
                          זוגי: {coach.price_duo}
                        </span>
                      )}
                      {coach.price_group && (
                        <span className="text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded-lg border border-amber-200 font-medium">
                          קבוצתי: {coach.price_group}
                        </span>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="mt-4 flex gap-2">
                      {(coach.whatsapp || coach.phone) && (
                        <div
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`https://wa.me/${getWhatsAppNumber(coach)}`, '_blank'); }}
                          className="flex-1 cursor-pointer"
                        >
                          <Button className="w-full bg-green-500 hover:bg-green-600 text-white text-sm" size="sm">
                            <MessageCircle className="w-4 h-4 ml-1" />
                            WhatsApp
                          </Button>
                        </div>
                      )}
                      {coach.phone && (
                        <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `tel:${coach.phone}`; }} className="flex-1 cursor-pointer">
                          <Button variant="outline" className="w-full border-emerald-400 text-emerald-600 hover:bg-emerald-50 text-sm" size="sm">
                            <Phone className="w-4 h-4 ml-1" />
                            התקשר
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachesPage;
